import { useMemo } from "react";
import { useFilterContext } from "../contexts/FilterProvider";

type FacetOption = {
	value: string | number;
	[key: string]: unknown;
};

type UsePinnedSelectedOptionsParams = {
	options: FacetOption[];
	filterId: string;
	enabled: boolean;
};

type UsePinnedSelectedOptionsResult = {
	pinnedOptions: FacetOption[];
	remainingOptions: FacetOption[];
};

/**
 * Splits a filter's options into the ones that were selected when the panel
 * opened (pinned on top) and the rest, preserving each group's existing order.
 *
 * The pinned set comes from the applied selection (URL) exposed by the filter
 * context, which stays frozen while the popover/drawer is open and only
 * refreshes on reopen — so checking/unchecking an option does not reorder the
 * list until the panel is reopened.
 */
export const usePinnedSelectedOptions = ({
	options,
	filterId,
	enabled,
}: UsePinnedSelectedOptionsParams): UsePinnedSelectedOptionsResult => {
	const { filters } = useFilterContext();

	return useMemo(() => {
		if (!enabled) {
			return { pinnedOptions: [], remainingOptions: options };
		}

		const appliedValues = filters?.[filterId];

		const selectedValues = new Set(
			(Array.isArray(appliedValues) ? appliedValues : []).map((value: unknown) =>
				String(value)
			)
		);

		if (selectedValues.size === 0) {
			return { pinnedOptions: [], remainingOptions: options };
		}

		const pinnedOptions: FacetOption[] = [];
		const remainingOptions: FacetOption[] = [];

		for (const option of options) {
			if (selectedValues.has(String(option.value))) {
				pinnedOptions.push(option);
			} else {
				remainingOptions.push(option);
			}
		}

		return { pinnedOptions, remainingOptions };
	}, [enabled, options, filters, filterId]);
};
